import { useMutation } from '@apollo/client';
import { Card, Link, Stack, Typography } from '@mui/material';
import { FormSubmitRow } from 'components/common/form/submit-row';
import { FormTextField } from 'components/views/forms/form-text-field';
import { useSnackbar } from 'providers/snackbar';
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import { TelnyxThrottling_telnyxThrottling } from 'types/apollo/TelnyxThrottling';
import {
  UpdateTelnyxThrottling,
  UpdateTelnyxThrottlingVariables,
} from 'types/apollo/UpdateTelnyxThrottling';
import { UpdateTelnyxThrottlingMutation } from './throttling.graphql';

const DEFAULT_VALUE = 60;
const MAX_BUCKET_MIN = 7;
const MAX_BUCKET_MAX = 300;
const WINDOW_MIN = 1;
const WINDOW_MAX = 3600;

interface ThrottlingFormData {
  maxBucketSize: string;
  windowInSeconds: string;
}

export const ThrottlingCard: FC<{
  campaignId: string;
  throttling: TelnyxThrottling_telnyxThrottling | null;
  refetch: () => Promise<unknown>;
  canEdit: boolean;
}> = ({ campaignId, throttling, refetch, canEdit }) => {
  const { openSnackbar } = useSnackbar();

  const form = useForm<ThrottlingFormData>({
    values: {
      maxBucketSize: throttling?.maxBucketSize?.toString() ?? '',
      windowInSeconds: throttling?.windowInSeconds?.toString() ?? '',
    },
  });
  // Lock inputs while a save is in flight; the post-mutation refetch would
  // otherwise re-sync the reactive `values` prop and clobber any edits the
  // user makes mid-flight.
  const isSubmitting = form.formState.isSubmitting;
  const inputsDisabled = !canEdit || isSubmitting;

  const [updateThrottling] = useMutation<UpdateTelnyxThrottling, UpdateTelnyxThrottlingVariables>(
    UpdateTelnyxThrottlingMutation,
  );

  const isProvisioned = throttling != null;
  // When neither field has a stored value, keep Save enabled even if the form
  // is pristine — lets the user save explicit defaults without first dirtying.
  const hasExistingValues =
    throttling != null && (throttling.maxBucketSize != null || throttling.windowInSeconds != null);

  const onSubmit = form.handleSubmit(async (values) => {
    await updateThrottling({
      variables: {
        campaignId,
        maxBucketSize: Number(values.maxBucketSize) || DEFAULT_VALUE,
        windowInSeconds: Number(values.windowInSeconds) || DEFAULT_VALUE,
      },
    });
    openSnackbar({
      message: 'Throttling values saved.',
      severity: 'success',
    });
    // Refetch is best-effort: the save already succeeded. A refetch failure
    // is reported by the global Apollo onError link; the form may briefly
    // show stale values until next navigation.
    try {
      await refetch();
    } catch {
      /* see comment above */
    }
  });

  return (
    <Card variant="outlined" sx={{ padding: '16px' }}>
      <Stack spacing={2}>
        <Typography variant="h5">Telnyx SMS Throttling</Typography>
        {!isProvisioned && (
          <Typography color="text.secondary">
            Telnyx is not provisioned for this campaign. Provision it from the{' '}
            <Link component={RouterLink} to="../vendors" relative="path">
              Vendors
            </Link>{' '}
            tab to configure throttling.
          </Typography>
        )}
        {isProvisioned && (
          <FormProvider {...form}>
            <form onSubmit={onSubmit}>
              <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
                <FormTextField
                  form={form}
                  name="maxBucketSize"
                  type="number"
                  optional
                  label="Max bucket size"
                  helperText="Range 7–300. Defaults to 60 if unset."
                  disabled={inputsDisabled}
                  inputProps={{ min: MAX_BUCKET_MIN, max: MAX_BUCKET_MAX }}
                  InputProps={{ placeholder: '60 (default)' }}
                  sx={{ width: 240 }}
                />
                <FormTextField
                  form={form}
                  name="windowInSeconds"
                  type="number"
                  optional
                  label="Window (seconds)"
                  helperText="Range 1–3600. Defaults to 60 if unset."
                  disabled={inputsDisabled}
                  inputProps={{ min: WINDOW_MIN, max: WINDOW_MAX }}
                  InputProps={{ placeholder: '60 (default)' }}
                  sx={{ width: 240 }}
                />
                {canEdit && (
                  <FormSubmitRow
                    submitText="Save"
                    enableIfNotDirty={!hasExistingValues}
                    onCancel={() => form.reset()}
                  />
                )}
              </Stack>
            </form>
          </FormProvider>
        )}
      </Stack>
    </Card>
  );
};
